import {useClient, useDictionary} from '@adamlip/okeoke.client';
import {Card, CardBody} from '@heroui/react';
import BarSoftButton from 'components/_NewComponents/BarSoftButton';
import FormattedNumber from 'components/FormattedNumber';
import MenuOptionsSelector from 'components/Modals/MenuSelectModal/MenuOptionsSelector';
import {AppParamsContext} from 'contexts/AppParamsContext';
import {MessageCircleQuestion} from 'lucide-react';
import {useContext, useEffect, useRef, useState} from 'react';
import toast from 'react-hot-toast';
import SendVibration from 'utils/Helpers-v1/SendVibration';

export default function MenuSelectModal({isOpen, setUserSelectedMenuOption, setIsOpen, setShowMenuOptions, showMenuOptions, sizes, selectedMenuSize, setSelectedMenuSize, closeItemModal, setSize, addItem, setNoMenuSelected}) {
	const {getValue} = useDictionary();
	const {getName} = useClient();
	const {desktopView} = useContext(AppParamsContext);
	const [localSizes, setLocalSizes] = useState(null);
	const [normalSizeCount, setNormalSizeCount] = useState(0);
	const [menuSizeCount, setMenuSizeCount] = useState(0);
	const [selectedAssembledItem, setSelectedAssembledItem] = useState(null);
	const sizeGroupsValidation = useRef({});
	const selectedAssembledItems = useRef({});

	const handleClickOnMenu = () => {
		setUserSelectedMenuOption(true);
		setShowMenuOptions(true);
		if (menuSizeCount === 1) {
			setSelectedMenuSize(localSizes?.filter((size) => size?.attributes?.menuSize)[0]);
		}
	};

	const handleClickOnNotMenu = () => {
		setUserSelectedMenuOption(true);
		setIsOpen(false);
		setNoMenuSelected(true);
	};

	const handleSelectSize = (size) => {
		setSelectedMenuSize(size);
		setSize(size.uuid);
	};

	const setSizeGroupsValidation = (groupUUID, valid) => {
		sizeGroupsValidation.current = {
			...sizeGroupsValidation.current,
			[groupUUID]: valid,
		};
	};

	const addToBasket = () => {
		if (selectedAssembledItem == null) {
			toast.error(getValue('app_v2_menu_no_menuSize_selected'));
		} else {
			if (!selectedAssembledItem?.valid) {
				toast.error(getValue('app_v2_menu_menuSize_invalid'));
			} else {
				let values = Object.values(sizeGroupsValidation.current);
				let invalid = values.some((value) => !value);

				if (invalid) {
					toast.error(getValue('app_v2_menu_invalid'));
				} else {
					let selectedItems = [];
					for (let group of Object.values(selectedAssembledItems.current)) {
						let items = Object.values(group)?.filter((item) => item.count > 0);
						selectedItems = [...selectedItems, ...items];
					}
					let parentItemUUID = addItem(selectedAssembledItem);
					if (parentItemUUID) {
						for (let item of selectedItems) {
							addItem(item, parentItemUUID);
						}
						toast.success(getValue('app_v2_add_menu_success'));
						SendVibration('success');

						setIsOpen(false);
						closeItemModal();
					} else {
						toast.error(getValue('app_v2_add_item_error'));
					}
				}
			}
		}
	};

	useEffect(() => {
		if (localSizes) {
			let normalCount = 0;
			let menuCount = 0;
			for (let size of localSizes) {
				if (size?.attributes?.menuSize) {
					menuCount++;
				} else {
					normalCount++;
				}
			}
			setNormalSizeCount(normalCount);
			setMenuSizeCount(menuCount);
		} else {
			setShowMenuOptions(false);
			setSelectedMenuSize(null);
			setNormalSizeCount(0);
			setMenuSizeCount(0);
		}
		//eslint-disable-next-line
	}, [localSizes]);

	useEffect(() => {
		if (normalSizeCount === 0 && menuSizeCount === 1) {
			setSelectedMenuSize(localSizes[0]);
			setShowMenuOptions(true);
		}
		//eslint-disable-next-line
	}, [normalSizeCount, menuSizeCount]);

	useEffect(() => {
		if (isOpen) {
			setLocalSizes([...sizes]);
		} else {
			setLocalSizes(null);
		}
	}, [isOpen, sizes]);

	return (
		<div className='w-full flex flex-col gap-2 items-start justify-end overflow-hidden'>
			{!showMenuOptions && (
				<div className='px-2 py-6 flex flex-col gap-2 items-center justify-center w-full'>
					<MessageCircleQuestion className='w-14 h-14 stroke-foreground/70' strokeWidth={1} />
					<div className='font-bold text-xl'>{getValue('app_v2_menu_select_menu_title')}</div>
					<div className='w-full text-center text-sm pb-4'>{getValue('app_v2_menu_select_menu_description')}</div>
					<BarSoftButton size='lg' color='primary' variant='solid' onPress={handleClickOnMenu}>
						{getValue('app_v2_menu_select_menu')}
					</BarSoftButton>
					<BarSoftButton size='lg' color='primary' variant='bordered' forceCustom={true} onPress={handleClickOnNotMenu}>
						{getValue('app_v2_menu_select_no_menu')}
					</BarSoftButton>
				</div>
			)}

			{showMenuOptions && (
				<>
					{selectedMenuSize?.uuid == null && (
						<div className='px-2 py-6 flex flex-col gap-2 items-center justify-center w-full'>
							<MessageCircleQuestion className='w-14 h-14 stroke-foreground/70' strokeWidth={1} />
							<div className='font-bold text-xl'>{getValue('app_v2_menu_select_menu_size_title')}</div>
							<div className='w-full text-center text-sm pb-4'>{getValue('app_v2_menu_select_menu_size_description')}</div>
							{localSizes
								?.filter((size) => size?.attributes?.menuSize)
								.map((size) => (
									<Card fullWidth className='shrink-0 bg-background border border-border rounded-general' isPressable key={size.uuid} onPress={() => handleSelectSize(size)}>
										<CardBody>
											<div className='font-semibold text-lg'>{getName(size?.name)}</div>
											<div>{getName(size?.description)}</div>
											<div className='w-full text-right'>
												<FormattedNumber value={size?.price} />
											</div>
										</CardBody>
									</Card>
								))}

							<BarSoftButton size='lg' onPress={handleClickOnNotMenu} color='primary' forceCustom={true} variant='bordered'>
								{getValue('app_v2_menu_select_back')}
							</BarSoftButton>
						</div>
					)}
					{selectedMenuSize?.uuid != null && (
						<MenuOptionsSelector addToBasket={addToBasket} selectedSize={selectedMenuSize} setSizeGroupsValidation={setSizeGroupsValidation} selectedAssembledItems={selectedAssembledItems} setSelectedAssembledItem={setSelectedAssembledItem} selectedAssembledItem={selectedAssembledItem} />
					)}
				</>
			)}
		</div>
	);
}
